import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FiSearch, FiFilter, FiMoreVertical, FiChevronLeft, FiChevronRight, FiLoader, FiChevronUp, FiChevronDown, FiChevronsUp, FiX } from 'react-icons/fi';
import ReactPaginate from 'react-paginate';

/**
 * Reusable DataTable component with search, column filters, pagination, and action menu.
 *
 * @param {Object} props
 * @param {Array<{
 *   key: string,
 *   header: string,
 *   render?: (row: any) => React.ReactNode,
 *   accessor?: (row: any) => string,
 *   filterable?: boolean,
 *   sortable?: boolean,
 *   className?: string,
 *   headerClassName?: string,
 * }>} props.columns - Column definitions.
 * @param {Array} props.data - Row data.
 * @param {string} props.rowKey - Unique field name for row identity (e.g. "id").
 * @param {boolean} [props.loading=false]
 * @param {string|null} [props.error=null]
 * @param {() => void} [props.onRetry] - Called when user clicks retry on error.
 * @param {boolean} [props.searchable=true]
 * @param {string} [props.searchPlaceholder="Search..."]
 * @param {(row: any) => Array<{label:string, icon?:any, onClick:()=>void, danger?:boolean, divider?:boolean}>} [props.actions]
 * @param {React.ReactNode} [props.headerActions] - Extra buttons rendered in the action bar (right side).
 * @param {number[]} [props.itemsPerPageOptions=[5,10,25,50]]
 * @param {number} [props.defaultItemsPerPage=10]
 * @param {string} [props.emptyMessage="No records found"]
 * @param {string} [props.totalLabel="items"]
 */
const DataTable = ({
  columns,
  data,
  rowKey,
  loading = false,
  error = null,
  onRetry,
  searchable = true,
  searchPlaceholder = 'Search...',
  actions,
  headerActions,
  belowSearchbarActions,
  itemsPerPageOptions = [5, 10, 25, 50],
  defaultItemsPerPage = 10,
  emptyMessage = 'No records found',
  totalLabel = 'items',
  rightAlignSearch = false,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
}) => {
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  // Sorting
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  // Column filters – two conditions per column
  const defaultFilterState = () => ({ operator1: 'contains', value1: '', logic: 'and', operator2: 'equals', value2: '' });

  const buildFilterMap = useCallback(() => {
    const map = {};
    columns.filter(c => c.filterable).forEach(c => { map[c.key] = defaultFilterState(); });
    return map;
  }, [columns]);

  const [columnFilters, setColumnFilters] = useState(buildFilterMap);
  const [appliedFilters, setAppliedFilters] = useState(buildFilterMap);
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
  const filterRef = useRef(null);

  // Action menu
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [appliedSearch, appliedFilters]);

  // Close popovers on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
      if (filterRef.current && !filterRef.current.contains(e.target)) setActiveFilter(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Search helpers
  const handleSearch = () => { setAppliedSearch(searchTerm.trim()); setCurrentPage(1); };
  const handleSearchKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

  // Condition evaluator
  const evalCondition = (cell, operator, fv) => {
    if (!fv) return null;
    const c = String(cell ?? '').toLowerCase();
    const v = fv.toLowerCase();
    switch (operator) {
      case 'contains': return c.includes(v);
      case 'equals': return c === v;
      case 'starts-with': return c.startsWith(v);
      case 'ends-with': return c.endsWith(v);
      case 'not-equals': return c !== v;
      case 'not-contains': return !c.includes(v);
      default: return null;
    }
  };

  const evalColumnFilter = (cellVal, filter) => {
    const r1 = evalCondition(cellVal, filter.operator1, filter.value1);
    const r2 = evalCondition(cellVal, filter.operator2, filter.value2);
    if (r1 === null && r2 === null) return true;
    if (r1 === null) return r2;
    if (r2 === null) return r1;
    return filter.logic === 'and' ? r1 && r2 : r1 || r2;
  };

  // Client-side filtering
  const filteredData = useMemo(() => {
    let rows = data ?? [];

    // Global search – search across all column accessors
    if (appliedSearch) {
      const term = appliedSearch.toLowerCase();
      rows = rows.filter(row => {
        const haystack = columns.map(c => {
          if (c.accessor) return String(c.accessor(row) ?? '');
          return String(row[c.key] ?? '');
        }).join(' ').toLowerCase();
        return haystack.includes(term);
      });
    }

    // Column-level filters
    const filterableColumns = columns.filter(c => c.filterable);
    for (const col of filterableColumns) {
      const filter = appliedFilters[col.key];
      if (!filter || (filter.value1 === '' && filter.value2 === '')) continue;
      rows = rows.filter(row => {
        const cellVal = col.accessor ? col.accessor(row) : (row[col.key] ?? '');
        return evalColumnFilter(cellVal, filter);
      });
    }

    return rows;
  }, [data, appliedSearch, appliedFilters, columns]);

  // Sorting
  const sortedData = useMemo(() => {
    if (!sortKey) return filteredData;
    const col = columns.find(c => c.key === sortKey);
    if (!col) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = col.accessor ? col.accessor(a) : (a[sortKey] ?? '');
      const bVal = col.accessor ? col.accessor(b) : (b[sortKey] ?? '');
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      let cmp;
      if (!isNaN(aNum) && !isNaN(bNum) && aVal !== '' && bVal !== '') {
        cmp = aNum - bNum;
      } else {
        cmp = aStr.localeCompare(bStr);
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sortKey, sortDirection, columns]);

  // Pagination
  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginatedData = sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Filter popover handlers
  const handleFilterClick = (e, columnKey) => {
    e.stopPropagation();
    if (activeFilter === columnKey) { setActiveFilter(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const popoverWidth = 260;
    let left = rect.left;
    if (left + popoverWidth > window.innerWidth) left = window.innerWidth - popoverWidth - 8;
    setFilterPosition({ top: rect.bottom + 4, left });
    setColumnFilters(f => ({ ...f, [columnKey]: { ...appliedFilters[columnKey] } }));
    setActiveFilter(columnKey);
  };

  const handleApplyFilter = () => {
    setAppliedFilters(f => ({ ...f, [activeFilter]: { ...columnFilters[activeFilter] } }));
    setActiveFilter(null);
  };

  const handleClearFilter = () => {
    const empty = defaultFilterState();
    setColumnFilters(f => ({ ...f, [activeFilter]: empty }));
    setAppliedFilters(f => ({ ...f, [activeFilter]: empty }));
    setActiveFilter(null);
  };

  const hasActiveFilter = (key) => appliedFilters[key]?.value1 !== '' || appliedFilters[key]?.value2 !== '';

  // Sort handler
  const handleSort = (columnKey) => {
    if (sortKey === columnKey) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        // Third click clears sort
        setSortKey(null);
        setSortDirection('asc');
      }
    } else {
      setSortKey(columnKey);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Action menu toggle
  const handleMenuToggle = (e, rowId) => {
    e.stopPropagation();
    const nextId = openMenuId === rowId ? null : rowId;
    if (nextId !== null) {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 176 });
    }
    setOpenMenuId(nextId);
  };

  // Selection helpers
  const isAllSelected = selectable && paginatedData.length > 0 && paginatedData.every(row => selectedRows.includes(row[rowKey]));
  const isSomeSelected = selectable && paginatedData.some(row => selectedRows.includes(row[rowKey])) && !isAllSelected;

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    const pageIds = paginatedData.map(row => row[rowKey]);
    if (isAllSelected) {
      onSelectionChange(selectedRows.filter(id => !pageIds.includes(id)));
    } else {
      const merged = [...new Set([...selectedRows, ...pageIds])];
      onSelectionChange(merged);
    }
  };

  const handleSelectRow = (id) => {
    if (!onSelectionChange) return;
    if (selectedRows.includes(id)) {
      onSelectionChange(selectedRows.filter(r => r !== id));
    } else {
      onSelectionChange([...selectedRows, id]);
    }
  };

  // ── Loading state: render a table-shaped skeleton to match layout ──
  if (loading) {
    const colCount = columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0);
    const rows = itemsPerPage || 5;
    return (
      <div className="rounded-xl shadow-sm border overflow-hidden bg-white border-[#919191]/30">
        <div className="p-4">
        
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#f6fbff]">
                  {Array.from({ length: colCount }).map((_, i) => (
                    <th key={i} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#919191]">
                      <div className="w-24 h-3 bg-[#e6eef5] rounded animate-pulse" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#919191]/10">
                {Array.from({ length: rows }).map((_, r) => (
                  <tr key={r} className="transition-colors">
                    {Array.from({ length: colCount }).map((__, c) => (
                      <td key={c} className="px-4 py-3">
                        <div className="w-full h-4 bg-[#e6eef5] rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-t border-[#919191]/20 bg-gradient-to-r from-[#919191]/5 to-transparent gap-3">
          <div className="flex items-center gap-2">
            <div className="w-20 h-4 bg-[#e6eef5] rounded animate-pulse" />
            <div className="w-12 h-4 bg-[#e6eef5] rounded animate-pulse" />
          </div>
          <div className="w-40 h-6 bg-[#e6eef5] rounded animate-pulse" />
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (error) {
    return (
      <div className="rounded-xl border p-4 bg-[#F47F2E]/10 border-[#F47F2E]/30 text-[#F47F2E]">
        Error: {error}
        {onRetry && <button onClick={onRetry} className="ml-4 underline">Retry</button>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Action bar: search + header actions */}
      {(searchable || headerActions) && (
       <div className={`flex flex-col items-center sm:justify-between gap-3 ${rightAlignSearch ? 'sm:flex-row-reverse' : 'sm:flex-row'}`}>
       
          
             {searchable && (
            <div className="flex items-center gap-2 flex-1 max-w-md">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#919191]" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="w-full pl-10 pr-8 py-2 rounded-l-lg text-sm border border-r-0 focus:outline-none focus:ring-2 focus:ring-[#0077b5] bg-white border-[#919191]/30 text-[#53545a]"
                />
                {searchTerm && (
                  <button
                    onClick={() => { setSearchTerm(''); setAppliedSearch(''); setCurrentPage(1); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#919191] hover:text-[#53545a] transition-colors"
                    title="Clear search"
                    type="button"
                  >
                    <FiX className="text-base" />
                  </button>
                )}
              </div>
              <button
                onClick={handleSearch}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0077b5] text-white rounded-r-lg text-sm font-medium hover:bg-[#005f8f] transition-colors shadow-sm border border-[#0077b5] -ml-2"
                title="Search"
              >
                <FiSearch className="text-base" />
                Search
              </button>
            </div>
          )}
          {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
        </div>
      )}
      {
        belowSearchbarActions && (  
          <>{belowSearchbarActions}</>
        )
      }

      {/* Table */}
      <div className="rounded-xl shadow-sm border overflow-hidden bg-white border-[#919191]/30">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#919191]/5">
                {selectable && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-[#919191]/50 text-[#0077b5] focus:ring-[#0077b5] cursor-pointer"
                    />
                  </th>
                )}
                {columns.map(col => (
                  <th key={col.key} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#919191] ${col.headerClassName || ''}`}>
                    <div className="flex items-center justify-between">
                      <span
                        className={col.sortable ? 'flex items-center gap-1.5 cursor-pointer select-none hover:text-[#53545a] transition-colors group' : 'flex items-center gap-1'}
                        onClick={col.sortable ? () => handleSort(col.key) : undefined}
                        title={col.sortable ? (sortKey === col.key ? (sortDirection === 'asc' ? 'Sorted ascending' : 'Sorted descending') : 'Click to sort') : undefined}
                      >
                        {col.header}
                        {col.sortable && sortKey === col.key && (
                          sortDirection === 'asc'
                            ? <FiChevronUp className="w-4 h-4 text-[#0077b5]" />
                            : <FiChevronDown className="w-4 h-4 text-[#0077b5]" />
                        )}
                        {col.sortable && sortKey !== col.key && (
                          <span className="flex flex-col -space-y-1.5">
                            <FiChevronUp className="w-3 h-3 text-[#919191]/60 group-hover:text-[#53545a]" />
                            <FiChevronDown className="w-3 h-3 text-[#919191]/60 group-hover:text-[#53545a]" />
                          </span>
                        )}
                      </span>
                      {col.filterable && (
                        <button
                          onClick={(e) => handleFilterClick(e, col.key)}
                          className={`p-0.5 rounded ${hasActiveFilter(col.key) ? 'text-[#0077b5]' : 'text-[#919191]/60 hover:text-[#919191]'}`}
                        >
                          <FiFilter className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
                {actions && (
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-[#919191]">Action</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#919191]/10">
              {paginatedData.map(row => {
                const id = row[rowKey];
                return (
                  <tr key={id} className={`transition-colors ${selectable && selectedRows.includes(id) ? 'bg-[#0077b5]/5' : 'hover:bg-[#919191]/5'}`}>
                    {selectable && (
                      <td className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(id)}
                          onChange={() => handleSelectRow(id)}
                          className="w-4 h-4 rounded border-[#919191]/50 text-[#0077b5] focus:ring-[#0077b5] cursor-pointer"
                        />
                      </td>
                    )}
                    {columns.map(col => (
                      <td key={col.key} className={`px-4 py-3 ${col.className || ''}`}>
                        {col.render ? col.render(row) : <span className="text-sm text-[#919191]">{row[col.key] ?? '-'}</span>}
                      </td>
                    ))}
                    {actions && (
                      <td className="px-4 py-3">
                        <div className="flex justify-center">
                          <button
                            onClick={(e) => handleMenuToggle(e, id)}
                            className="p-1.5 rounded-lg transition-colors text-[#919191] hover:text-[#53545a] hover:bg-[#919191]/10"
                          >
                            <FiMoreVertical className="text-lg" />
                          </button>
                          {openMenuId === id && (
                            <div
                              ref={menuRef}
                              style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left }}
                              className="z-[9999] w-44 rounded-lg shadow-lg border bg-white border-[#919191]/30"
                            >
                              {actions(row).map((item, idx) =>
                                item.divider ? (
                                  <hr key={`d-${idx}`} className="border-[#919191]/10" />
                                ) : (
                                  <button
                                    key={item.label}
                                    onClick={() => { setOpenMenuId(null); item.onClick(); }}
                                    className={`w-full flex items-center gap-1.5 px-3 py-1 text-sm transition-colors ${
                                      item.danger
                                        ? 'text-[#F47F2E] hover:bg-[#F47F2E]/10'
                                        : 'text-[#53545a] hover:bg-[#919191]/5'
                                    }`}
                                  >
                                    {item.icon && <item.icon className="text-sm flex-shrink-0" />}
                                    {item.label}
                                  </button>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan={columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)} className="px-4 py-12 text-center">
                    <p className="text-sm text-[#919191]">{emptyMessage}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Column filter popover */}
        {activeFilter && (
          <div ref={filterRef} style={{ position: 'fixed', top: filterPosition.top, left: filterPosition.left, zIndex: 99999 }} className="w-64 rounded-lg shadow-lg border bg-white border-[#919191]/30 p-4">
            <p className="text-sm font-semibold text-[#53545a] mb-3">Filter</p>

            <select value={columnFilters[activeFilter]?.operator1} onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], operator1: e.target.value } }))} className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-[#919191]/30 text-[#53545a] mb-2">
              <option value="contains">Contains</option>
              <option value="equals">Equals</option>
              <option value="starts-with">Starts with</option>
              <option value="ends-with">Ends with</option>
              <option value="not-equals">Not equals</option>
              <option value="not-contains">Does not contain</option>
            </select>
            <input type="text" autoFocus placeholder="" value={columnFilters[activeFilter]?.value1} onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], value1: e.target.value } }))} className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-[#919191]/30 text-[#53545a] mb-3" />

            <select value={columnFilters[activeFilter]?.logic} onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], logic: e.target.value } }))} className="w-24 px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-[#919191]/30 text-[#53545a] mb-3">
              <option value="and">And</option>
              <option value="or">Or</option>
            </select>

            <select value={columnFilters[activeFilter]?.operator2} onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], operator2: e.target.value } }))} className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-[#919191]/30 text-[#53545a] mb-2">
              <option value="contains">Contains</option>
              <option value="equals">Equals</option>
              <option value="starts-with">Starts with</option>
              <option value="ends-with">Ends with</option>
              <option value="not-equals">Not equals</option>
              <option value="not-contains">Does not contain</option>
            </select>
            <input type="text" placeholder="" value={columnFilters[activeFilter]?.value2} onChange={(e) => setColumnFilters(f => ({ ...f, [activeFilter]: { ...f[activeFilter], value2: e.target.value } }))} className="w-full px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#0077b5] bg-white border-[#919191]/30 text-[#53545a] mb-4" />

            <div className="flex items-center justify-between">
              <button onClick={handleClearFilter} className="px-4 py-1.5 text-sm font-semibold border border-[#F47F2E] text-[#F47F2E] rounded hover:bg-[#F47F2E]/10 transition-colors">CLEAR</button>
              <button onClick={handleApplyFilter} className="px-4 py-1.5 text-sm font-semibold border border-[#F47F2E] text-[#F47F2E] rounded hover:bg-[#F47F2E]/10 transition-colors">APPLY</button>
            </div>
          </div>
        )}

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-4 border-t border-[#919191]/20 bg-gradient-to-r from-[#919191]/5 to-transparent gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#919191]">Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077b5]/40 bg-white border-[#919191]/30 text-[#53545a] cursor-pointer hover:border-[#0077b5]/50 transition-colors"
            >
              {itemsPerPageOptions.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="text-sm text-[#919191]">
              of <span className="font-semibold text-[#53545a]">{totalItems}</span> {totalLabel}
            </span>
          </div>
          <ReactPaginate
            pageCount={totalPages || 1}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            onPageChange={({ selected }) => setCurrentPage(selected + 1)}
            forcePage={currentPage - 1}
            containerClassName="rc-pagination"
            pageClassName="rc-pagination-page"
            pageLinkClassName="rc-pagination-link"
            activeClassName="rc-pagination-active"
            previousClassName="rc-pagination-prev"
            nextClassName="rc-pagination-next"
            previousLinkClassName="rc-pagination-link"
            nextLinkClassName="rc-pagination-link"
            breakClassName="rc-pagination-break"
            breakLinkClassName="rc-pagination-link"
            disabledClassName="rc-pagination-disabled"
            previousLabel={<FiChevronLeft />}
            nextLabel={<FiChevronRight />}
          />
        </div>
      </div>
    </div>
  );
};

export default DataTable;
